import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtConfig, JwtHelperService } from '@auth0/angular-jwt';
import { UnauthenticatedError } from '@salesrun/ngx-logging/errors/UnauthenticatedError';
import { UnauthorizedError } from '@salesrun/ngx-logging/errors/UnauthorizedError';
import { SessionTimeout } from '@shared/models.js';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { appConfig } from '../../app/app.config.mjs';
import { IJwtResponse } from './IJwtResponse';
import { JwtStorageService } from './jwt-storage.service';




export function jwtOptionsFactory(jwtStorage: JwtStorageService): JwtConfig {
    const url = new URL(appConfig.backendUrl);
    return {
        tokenGetter: () => jwtStorage.get(),
        allowedDomains: [
            url.host,
            /^(.*\.)?salesrun\.com$/,
            /^(.*\.)?symfa\.com$/,
            /^(.*\.)?aisnovations\.com$/,
            /^localhost(:[0-9]{1,4})?$/,
            /\.loc$/,
            /\.loca\.lt$/,
        ],
    };
}

@Injectable()
export class JwtService {

    constructor(private jwtHelper: JwtHelperService, private jwtStorage: JwtStorageService, private _http: HttpClient) { }

    get token() {
        const token = this.jwtStorage.get();
        return token ? token : null;
    }
    set token(value: string) {
        this.jwtStorage.set(value);
    }

    getRawClaims(): Map<string, string> | null {
        if (!this.isTokenExists()) {
            return null;
        }
        const raw = this.jwtHelper.decodeToken(this.token);
        return new Map<string, string>(Object.entries(raw));
    }

    getSessionTimeout(): SessionTimeout {
        const claims = this.getRawClaims();
        if (!claims) {
            return { expiration: 0, warning: 0 };
        }
        return JSON.parse(claims.get('SalesRun.Tenant.SessionTimeout'));
    }

    getExpirationDate(): number {
        const claims = this.getRawClaims();
        if (!claims) {
            return null;
        }
        return +claims.get('exp');
    }

    isTokenExists(): boolean {
        return !!this.token;
    }

    isTokenExpired(): boolean {
        return this.jwtHelper.isTokenExpired(this.token);
    }


    refreshToken(): Observable<void> {
        return this._http.get<IJwtResponse>('/api/auth/refresh-token')
            .pipe(
                tap(jwtToken => this.jwtStorage.set(jwtToken.jwt)),
                map(() => null),
                catchError(error => {
                    if (error instanceof UnauthorizedError) {
                        return throwError(() => new UnauthenticatedError(error.message));
                    }
                    return throwError(() => error);
                }),
            );
    }

    logout(): void {
        this.jwtStorage.delete();
        location.href = '/logout';
    }
}

